import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, Link as LinkIcon, Search, Calendar, RefreshCw, IndianRupee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BILLING_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One Time' },
];

const ClientServices = () => {
  const [clientServices, setClientServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCS, setEditingCS] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    client_id: '',
    service_id: '',
    custom_rate: '',
    billing_frequency: 'monthly',
    is_recurring: true,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [csRes, clientsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/client-services`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/services`),
      ]);
      setClientServices(csRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      service_id: '',
      custom_rate: '',
      billing_frequency: 'monthly',
      is_recurring: true,
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
    });
    setEditingCS(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_id: formData.client_id,
        service_id: formData.service_id,
        custom_rate: parseFloat(formData.custom_rate),
        billing_frequency: formData.billing_frequency,
        is_recurring: formData.is_recurring,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
      };

      if (editingCS) {
        await axios.put(`${API}/client-services/${editingCS.id}`, payload);
      } else {
        await axios.post(`${API}/client-services`, payload);
      }

      fetchAll();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error saving client service:', error);
      alert(error.response?.data?.detail || 'Error saving client service');
    }
  };

  const handleEdit = (cs) => {
    setEditingCS(cs);
    setFormData({
      client_id: cs.client_id,
      service_id: cs.service_id,
      custom_rate: cs.custom_rate?.toString() || '',
      billing_frequency: cs.billing_frequency || 'monthly',
      is_recurring: cs.is_recurring !== false,
      start_date: cs.start_date || new Date().toISOString().split('T')[0],
      end_date: cs.end_date || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (csId) => {
    if (window.confirm('Are you sure you want to remove this client service assignment?')) {
      try {
        await axios.delete(`${API}/client-services/${csId}`);
        fetchAll();
      } catch (error) {
        console.error('Error deleting client service:', error);
      }
    }
  };

  // Pre-fill rate when service is selected
  const handleServiceSelect = (serviceId) => {
    setFormData((prev) => {
      const svc = services.find((s) => s.id === serviceId);
      return {
        ...prev,
        service_id: serviceId,
        custom_rate: svc ? svc.default_rate.toString() : prev.custom_rate,
      };
    });
  };

  const filtered = clientServices.filter((cs) => {
    const term = searchTerm.toLowerCase();
    const clientName = cs.client?.name?.toLowerCase() || '';
    const serviceName = cs.service?.name?.toLowerCase() || '';
    return clientName.includes(term) || serviceName.includes(term);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="client-services-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-services-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client Services</h1>
          <p className="text-gray-500 text-sm mt-1">Assign and manage services for your clients</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="add-client-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Assign Service
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search by client or service name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="client-services-search"
        />
      </div>

      {/* Prerequisite check */}
      {(clients.length === 0 || services.length === 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <p className="text-amber-800 text-sm font-medium">
              {clients.length === 0 && services.length === 0
                ? 'Please add at least one Client and one Service before assigning client services.'
                : clients.length === 0
                ? 'Please add at least one Client before assigning services.'
                : 'Please add at least one Service before assigning to clients.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="client-services-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Client</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Service</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Rate</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Billing</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Recurring</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Start Date</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((cs) => (
                    <tr key={cs.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{cs.client?.name || '—'}</span>
                        {cs.client?.organization_name && (
                          <span className="block text-xs text-gray-500">{cs.client.organization_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{cs.service?.name || '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <span className="inline-flex items-center">
                          <IndianRupee className="w-3 h-3 mr-0.5" />
                          {cs.custom_rate?.toLocaleString('en-IN') || '0'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                          {cs.billing_frequency?.replace('_', ' ') || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cs.is_recurring ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {cs.is_recurring ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{cs.start_date || '—'}</td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(cs)} data-testid={`edit-cs-${cs.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(cs.id)} data-testid={`delete-cs-${cs.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <LinkIcon className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">
              {searchTerm ? 'No matching client services' : 'No client services assigned yet'}
            </h3>
            <p className="text-gray-400 text-sm mt-1 max-w-sm">
              {searchTerm
                ? 'Try adjusting your search.'
                : 'Assign a service to a client to start tracking billing and recurring work.'}
            </p>
            {!searchTerm && clients.length > 0 && services.length > 0 && (
              <Button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Assign First Service
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { resetForm(); } setShowForm(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="client-service-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingCS ? 'Edit Client Service' : 'Assign Service to Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Client Select */}
            <div>
              <Label>Client</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                <SelectTrigger data-testid="select-client">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.organization_name ? ` — ${c.organization_name}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service Select */}
            <div>
              <Label>Service</Label>
              <Select value={formData.service_id} onValueChange={handleServiceSelect}>
                <SelectTrigger data-testid="select-service">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — Rs.{s.default_rate}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rate & Billing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Custom Rate (Rs.)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.custom_rate}
                  onChange={(e) => setFormData({ ...formData, custom_rate: e.target.value })}
                  required
                  data-testid="input-custom-rate"
                />
              </div>
              <div>
                <Label>Billing Frequency</Label>
                <Select value={formData.billing_frequency} onValueChange={(v) => setFormData({ ...formData, billing_frequency: v })}>
                  <SelectTrigger data-testid="select-billing-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  data-testid="input-start-date"
                />
              </div>
              <div>
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="input-end-date"
                />
              </div>
            </div>

            {/* Recurring Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                data-testid="checkbox-recurring"
              />
              <Label htmlFor="is_recurring" className="cursor-pointer">Recurring service</Label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-client-service">
                {editingCS ? 'Update' : 'Assign Service'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientServices;
